import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Link2, Megaphone, Pencil, Send, Users, UserX, X } from "lucide-react";
import { toast } from "sonner";
import { API, errText, num, when } from "@/components/bulk/common";
import { CampaignList } from "@/components/bulk/CampaignList";
import { ContactsPanel } from "@/components/bulk/ContactsPanel";
import { TemplateGuide } from "@/components/bulk/TemplateGuide";
import { SpecialDays } from "@/components/bulk/SpecialDays";

const TABS = [["campaigns", "Kampanyalar"], ["days", "Özel günler"], ["contacts", "Kişiler"], ["guide", "Şablon kurulumu"]];

const QUALITY = { HIGH: ["Yüksek", "good"], MEDIUM: ["Orta", "mid"], LOW: ["Düşük", "low"] };
const GROWTH = "Meta sınırı 250'den 2.000'e işletme doğrulamasıyla ya da 30 günde 2.000 farklı kişiye mesaj iletilince çıkar; sonra kalite yüksek kaldıkça ve 7 günde sınırın yarısı kullanıldıkça 10.000 → 100.000 → sınırsız olur. Yükselince burası kendiliğinden güncellenir.";

function LimitSource({ info }) {
  const meta = info.meta, q = QUALITY[meta?.quality];
  const text = info.limit_mode === "auto" && meta?.limit ? `Meta'ya bağlı · Meta sınırı ${num(meta.limit)}, ${info.reserve} kişi diğer mesajlara ayrıldı` : meta?.limit ? `Elle ayarlandı · Meta sınırı ${num(meta.limit)}` : "Meta sınırı okunamadı · elle ayarlanan sınır kullanılıyor";
  return <><small title={GROWTH} data-testid="bulk-limit-source">{text}</small>{q && <small className={`quality-pill ${q[1]}`} title="Meta'nın mesaj kalitesi puanı" data-testid="bulk-limit-quality">Kalite: {q[0]}</small>}</>;
}

function LimitCard({ info, onSaved }) {
  const [edit, setEdit] = useState(false), [v, setV] = useState("");
  const put = async (body, ok) => {
    try { const r = await axios.put(`${API}/bulk/settings`, body); onSaved(r.data); setEdit(false); toast.success(ok); }
    catch (e) { toast.error(errText(e)); }
  };
  const save = () => (Number(v) >= 1 ? put({ mode: "manual", daily_limit: Number(v) }, "Günlük sınır elle ayarlandı") : toast.error("Günlük sınır en az 1 olmalı"));
  return (
    <div className="metric" data-testid="bulk-limit-card">
      <div className="metric-top"><span>Günlük gönderim sınırı</span>{!edit && <button className="icon-btn" onClick={() => { setV(info.daily_limit); setEdit(true); }} data-testid="bulk-limit-edit"><Pencil size={14} /></button>}</div>
      {edit ? (
        <div className="limit-edit"><input type="number" min="1" value={v} onChange={(e) => setV(e.target.value)} data-testid="bulk-limit-input" autoFocus />
          <button className="icon-btn" onClick={save} title="Elle ayarla" data-testid="bulk-limit-save"><Check size={16} /></button><button className="icon-btn" onClick={() => setEdit(false)} data-testid="bulk-limit-cancel"><X size={16} /></button></div>
      ) : <strong data-testid="bulk-limit-value">{num(info.daily_limit)}</strong>}
      {edit && info.meta?.limit && info.limit_mode !== "auto" && <button className="text-btn limit-auto-btn" onClick={() => put({ mode: "auto" }, "Sınır Meta'ya bağlandı")} data-testid="bulk-limit-auto"><Link2 size={13} />Meta'ya otomatik bağla</button>}
      <LimitSource info={info} />
    </div>
  );
}

function Stats({ info, onSaved }) {
  const c = info.contacts;
  return (
    <div className="metric-grid bulk-stats">
      <div className="metric" data-testid="bulk-active-card"><div className="metric-top"><span>Mesaj gidecek kişi</span><Users size={18} /></div><strong data-testid="bulk-active-count">{num(c.active)}</strong><small>Listede toplam {num(c.total)} kişi</small></div>
      <div className="metric" data-testid="bulk-optout-card"><div className="metric-top"><span>Listeden çıkan</span><UserX size={18} /></div><strong data-testid="bulk-optout-count">{num(c.opted_out)}</strong><small>Bu kişilere mesaj gönderilmez</small></div>
      <div className="metric" data-testid="bulk-sent-card"><div className="metric-top"><span>Son 24 saatte gönderilen</span><Send size={18} /></div><strong data-testid="bulk-sent-24h">{num(info.sent_24h)}</strong>
        <small>{info.remaining > 0 ? `Şu an ${num(info.remaining)} mesaj daha gidebilir` : `Sınır doldu · ${when(info.resume_at)} civarı devam`}</small></div>
      <LimitCard info={info} onSaved={onSaved} />
    </div>
  );
}

export default function BulkMessages() {
  const [tab, setTab] = useState("campaigns"), [info, setInfo] = useState(null);
  const load = () => axios.get(`${API}/bulk/settings`).then((r) => setInfo(r.data)).catch(() => toast.error("Bilgiler yüklenemedi, sayfayı yenileyin"));
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="page-title"><div><span className="eyebrow">ANA HAT · WHATSAPP</span><h1 data-testid="page-title">Toplu Mesaj</h1><p>Rehberinizdeki kişilere 29 Ekim, bayramlar gibi özel günlerde görselli WhatsApp mesajı gönderin.</p></div></div>
      {info && <Stats info={info} onSaved={setInfo} />}
      {info && !info.template_ready && (
        <div className="info-banner warn bulk-warning" data-testid="bulk-template-warning"><Megaphone size={20} /><div><b>Toplu mesaj şablonu henüz bağlı değil</b>
          <p>Kampanya hazırlayıp zamanlayabilirsiniz; mesajlar şablon Meta onayından geçip şablon kodu girilene kadar bekler.</p></div>
          <button className="outline-btn" onClick={() => setTab("guide")} data-testid="bulk-open-guide">Nasıl yapılır?</button></div>
      )}
      <div className="toolbar bulk-tabs">{TABS.map(([k, l]) => <button key={k} className={tab === k ? "filter-btn active" : "filter-btn"} onClick={() => setTab(k)} data-testid={`bulk-tab-${k}`}>{l}</button>)}</div>
      {tab === "campaigns" && info && <CampaignList info={info} onChanged={load} />}
      {tab === "days" && info && <SpecialDays info={info} onChanged={load} />}
      {tab === "contacts" && <ContactsPanel onChanged={load} />}
      {tab === "guide" && <TemplateGuide />}
    </>
  );
}
